/**
 * Cash Advance Ledger — Gap 7 UI.
 *
 * Lifecycle: Requested → Approved → Disbursed → Settled
 * Backend: /app/backend/routers/cash_advances.py
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Wallet, Plus, X, Filter, IndianRupee, Banknote, History,
  CheckCircle2, XCircle, ArrowDownToLine, Loader2, FileText, Receipt,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { formatINR } from '../../lib/format';
import { useAuth } from '../../contexts/AuthContext';


const STATUS_BADGE = {
  Requested: 'bg-amber-50 text-amber-700 border-amber-200',
  Approved: 'bg-teal-50 text-teal-700 border-teal-200',
  Disbursed: 'bg-teal-50 text-teal-700 border-teal-200',
  Settled: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-rose-50 text-rose-700 border-rose-200',
};

const FINANCE_DEPTS = new Set(['Finance & Accounts', 'Finance', 'Senior Leadership']);
const FINANCE_ROLES = new Set(['super_admin', 'cfo', 'finance_head', 'finance_manager', 'finance', 'md', 'ceo']);

function Tile({ label, value, sub, accent = 'slate', testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-display">{label}</div>
      <div className={`text-2xl font-bold mt-1 text-${accent}-700 tabular-nums`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest font-display text-slate-500">{label}</label>
    {children}
  </div>
);

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { dateStyle: 'medium' }); }
  catch { return iso; }
};


export default function CashAdvances() {
  const { user } = useAuth();
  const isFinance = useMemo(() => {
    const role = String(user?.role || '').toLowerCase();
    const dept = String(user?.department || '');
    return FINANCE_ROLES.has(role) || FINANCE_DEPTS.has(dept);
  }, [user]);

  const [tab, setTab] = useState(isFinance ? 'all' : 'mine');
  const [filter, setFilter] = useState({ status: '', outstanding_only: false });
  const [rows, setRows] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [outstandingByEmp, setOutstandingByEmp] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeAdvanceId, setActiveAdvanceId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.outstanding_only) params.outstanding_only = true;
      if (tab === 'mine' && user?.employee_id) params.employee_id = user.employee_id;
      const data = await apiGet('/cash-advances', params);
      setRows(data.items || []);
      setTotalOutstanding(data.total_outstanding || 0);

      if (isFinance && tab === 'rollup') {
        const r = await apiGet('/cash-advances/dashboard/outstanding-by-employee');
        setOutstandingByEmp(r.items || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load advances');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tab, filter.status, filter.outstanding_only]);

  return (
    <div className="p-6 space-y-4" data-testid="cash-advance-page">
      <PageHeader
        title="Cash Advances"
        subtitle="Issue, disburse, and settle employee cash advances. Outstanding tracked live as expenses are filed."
        icon={<Wallet size={18} />}
        actions={
          <button
            onClick={() => setShowForm(true)}
            className="px-3 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-xs font-display font-bold flex items-center gap-1.5"
            data-testid="cash-advance-new-btn"
          >
            <Plus size={14} /> Request Advance
          </button>
        }
      />

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          label={tab === 'mine' ? 'My Open Advances' : 'Open Advances'}
          value={rows.filter(r => r.status === 'Disbursed').length}
          accent="indigo"
          testId="cash-advance-tile-open"
        />
        <Tile
          label="Total Outstanding"
          value={formatINR(totalOutstanding)}
          accent="rose"
          testId="cash-advance-tile-outstanding"
        />
        <Tile
          label="Pending Approval"
          value={rows.filter(r => r.status === 'Requested').length}
          accent="amber"
          testId="cash-advance-tile-pending"
        />
        <Tile
          label="Settled · Last 30d"
          value={rows.filter(r => r.status === 'Settled' && r.settled_at && (Date.now() - new Date(r.settled_at).getTime()) < 30 * 86400000).length}
          accent="emerald"
          testId="cash-advance-tile-settled"
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        <TabBtn active={tab === 'mine'} onClick={() => setTab('mine')} testId="cash-advance-tab-mine">My Advances</TabBtn>
        {isFinance && <TabBtn active={tab === 'all'} onClick={() => setTab('all')} testId="cash-advance-tab-all">All Advances</TabBtn>}
        {isFinance && <TabBtn active={tab === 'rollup'} onClick={() => setTab('rollup')} testId="cash-advance-tab-rollup">Outstanding · By Employee</TabBtn>}
      </div>

      {tab === 'rollup' ? (
        <RollupTable rows={outstandingByEmp} loading={loading} />
      ) : (
        <>
          <FilterBar filter={filter} setFilter={setFilter} />
          <AdvancesTable rows={rows} loading={loading} onOpen={setActiveAdvanceId} />
        </>
      )}

      {showForm && (
        <IssueAdvanceModal
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); refresh(); }}
          defaultEmployee={user}
        />
      )}
      {activeAdvanceId && (
        <AdvanceDetailDrawer
          advanceId={activeAdvanceId}
          isFinance={isFinance}
          onClose={() => setActiveAdvanceId(null)}
          onAction={refresh}
        />
      )}
    </div>
  );
}


function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${
        active
          ? 'border-[#1F7A6E] text-[#1F7A6E]'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      {children}
    </button>
  );
}


function FilterBar({ filter, setFilter }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Filter size={14} className="text-slate-400" />
      <select
        value={filter.status}
        onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}
        className="px-2 py-1 border border-slate-300 rounded-sm text-xs"
        data-testid="cash-advance-filter-status"
      >
        <option value="">All statuses</option>
        {['Requested', 'Approved', 'Disbursed', 'Settled', 'Rejected'].map(s => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <label className="flex items-center gap-1.5 text-xs">
        <input
          type="checkbox"
          checked={filter.outstanding_only}
          onChange={(e) => setFilter(f => ({ ...f, outstanding_only: e.target.checked }))}
          data-testid="cash-advance-filter-outstanding"
        />
        Outstanding only
      </label>
    </div>
  );
}


function AdvancesTable({ rows, loading, onOpen }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <table className="w-full text-sm" data-testid="cash-advance-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
          <tr>
            <th className="text-left px-3 py-2">Advance #</th>
            <th className="text-left px-3 py-2">Employee</th>
            <th className="text-left px-3 py-2">Project / Purpose</th>
            <th className="text-right px-3 py-2">Amount</th>
            <th className="text-right px-3 py-2">Outstanding</th>
            <th className="text-left px-3 py-2">Expected By</th>
            <th className="text-center px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan="7" className="text-center py-6 text-slate-400">
              <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
            </td></tr>
          )}
          {!loading && !rows.length && (
            <tr><td colSpan="7" className="text-center text-slate-400 italic py-6 text-sm">No cash advances yet.</td></tr>
          )}
          {!loading && rows.map(r => (
            <tr
              key={r.id}
              className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
              onClick={() => onOpen(r.id)}
              data-testid={`cash-advance-row-${r.id}`}
            >
              <td className="px-3 py-2 text-xs font-mono">{r.advance_no}</td>
              <td className="px-3 py-2 text-xs">{r.employee_name}</td>
              <td className="px-3 py-2 text-xs">
                <div className="truncate max-w-[260px]">{r.project_name || '—'}</div>
                <div className="text-slate-400 truncate max-w-[260px]">{r.purpose}</div>
              </td>
              <td className="px-3 py-2 text-xs text-right font-bold tabular-nums">{formatINR(r.amount)}</td>
              <td className="px-3 py-2 text-xs text-right tabular-nums">
                {r.status === 'Disbursed' || r.status === 'Settled'
                  ? <span className={r.outstanding_amount > 0 ? 'text-rose-600 font-bold' : 'text-emerald-600 font-bold'}>{formatINR(r.outstanding_amount)}</span>
                  : <span className="text-slate-400">—</span>}
                {r.over_settlement_amount > 0 && (
                  <div className="text-[10px] text-amber-600">Over by {formatINR(r.over_settlement_amount)}</div>
                )}
              </td>
              <td className="px-3 py-2 text-xs">{fmtDate(r.expected_settlement_date)}</td>
              <td className="px-3 py-2 text-center">
                <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase border ${STATUS_BADGE[r.status] || 'bg-slate-100'}`}>
                  {r.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function RollupTable({ rows, loading }) {
  const grand = rows.reduce((s, r) => s + (r.outstanding || 0), 0);
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="cash-advance-rollup-table">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-600">Outstanding · By Employee</div>
        <div className="text-xs">
          Grand Total: <span className="font-bold text-rose-600 tabular-nums">{formatINR(grand)}</span>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 font-display">
          <tr>
            <th className="text-left px-3 py-2">Employee</th>
            <th className="text-right px-3 py-2">Advances</th>
            <th className="text-right px-3 py-2">Outstanding</th>
            <th className="text-left px-3 py-2">Oldest Disbursed</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan="4" className="text-center py-6 text-slate-400"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</td></tr>}
          {!loading && !rows.length && <tr><td colSpan="4" className="text-center text-slate-400 italic py-6 text-sm">No outstanding advances.</td></tr>}
          {!loading && rows.map(r => (
            <tr key={r.employee_id} className="border-t border-slate-100">
              <td className="px-3 py-2 text-xs">{r.employee_name}</td>
              <td className="px-3 py-2 text-xs text-right tabular-nums">{r.advances_count}</td>
              <td className="px-3 py-2 text-xs text-right font-bold tabular-nums text-rose-600">{formatINR(r.outstanding)}</td>
              <td className="px-3 py-2 text-xs">{fmtDate(r.oldest_disbursed_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ─────────────────────────── Issue Advance form ───────────────────────────
function IssueAdvanceModal({ onClose, onSaved, defaultEmployee }) {
  const [form, setForm] = useState({
    employee_id: defaultEmployee?.employee_id || '',
    employee_name: defaultEmployee?.name || '',
    amount: 0,
    purpose: '',
    project_id: '',
    project_name: '',
    expected_settlement_date: '',
  });
  const [employees, setEmployees] = useState([]);
  const [assignees, setAssignees] = useState({ projects: [] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiGet('/hr/employees').then((list) => setEmployees(Array.isArray(list) ? list : [])).catch(() => {});
    apiGet('/petty-cash/assignees').then((r) => setAssignees({
      projects: Array.isArray(r?.projects) ? r.projects : [],
    })).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onPickEmployee = (id) => {
    const emp = employees.find(e => e.id === id || e.emp_id === id);
    set('employee_id', id);
    set('employee_name', emp?.name || '');
  };
  const onPickProject = (id) => {
    const p = assignees.projects.find(p => p.id === id);
    set('project_id', id);
    set('project_name', p?.name || '');
  };

  const save = async () => {
    if (!form.employee_id) return toast.error('Pick the employee receiving the advance');
    if (!form.employee_name) return toast.error('Employee name missing');
    if (!form.amount || Number(form.amount) <= 0) return toast.error('Amount must be greater than 0');
    if ((form.purpose || '').trim().length < 4) return toast.error('Purpose must be at least 4 characters');

    setSubmitting(true);
    try {
      const payload = {
        employee_id: form.employee_id,
        employee_name: form.employee_name,
        amount: Number(form.amount),
        purpose: form.purpose.trim(),
        project_id: form.project_id || null,
        project_name: form.project_name || null,
        expected_settlement_date: form.expected_settlement_date || null,
      };
      await apiPost('/cash-advances', payload);
      toast.success('Cash advance requested');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to request advance');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end md:items-center justify-center p-3">
      <div className="bg-white rounded-md w-full max-w-xl max-h-[92vh] overflow-y-auto" data-testid="cash-advance-form">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg">Request Cash Advance</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Employee">
            <select
              value={form.employee_id}
              onChange={(e) => onPickEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              data-testid="cash-advance-form-employee"
            >
              <option value="">Select employee…</option>
              {employees.map(e => (
                <option key={e.id || e.emp_id} value={e.id || e.emp_id}>
                  {e.name}{e.emp_id ? ` (${e.emp_id})` : ''}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (₹)">
              <input
                type="number"
                value={form.amount}
                onChange={(e) => set('amount', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                data-testid="cash-advance-form-amount"
              />
            </Field>
            <Field label="Expected Settlement Date">
              <input
                type="date"
                value={form.expected_settlement_date}
                onChange={(e) => set('expected_settlement_date', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                data-testid="cash-advance-form-expected-date"
              />
            </Field>
          </div>
          <Field label="Project (optional)">
            <select
              value={form.project_id}
              onChange={(e) => onPickProject(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              data-testid="cash-advance-form-project"
            >
              <option value="">— No project —</option>
              {assignees.projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ''}</option>
              ))}
            </select>
          </Field>
          <Field label={`Purpose (min 4 chars · ${(form.purpose || '').length}/4)`}>
            <textarea
              rows={3}
              value={form.purpose}
              onChange={(e) => set('purpose', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              placeholder="e.g. Site cash for material run · Pune project · weekend"
              data-testid="cash-advance-form-purpose"
            />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button
            disabled={submitting}
            onClick={save}
            className="px-4 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-sm font-display font-bold disabled:opacity-50"
            data-testid="cash-advance-form-submit"
          >
            {submitting ? 'Saving…' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────── Detail Drawer ───────────────────────────
function AdvanceDetailDrawer({ advanceId, isFinance, onClose, onAction }) {
  const [adv, setAdv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettle, setShowSettle] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/cash-advances/${advanceId}`);
      setAdv(data);
    } catch (e) {
      toast.error('Failed to load advance');
      onClose();
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [advanceId]);

  const act = async (label, fn) => {
    try {
      await fn();
      toast.success(`${label} done`);
      await refresh();
      onAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed`);
    }
  };

  const approve = () => act('Approve', () => apiPut(`/cash-advances/${advanceId}/approve`));
  const reject = () => {
    const reason = window.prompt('Reason for rejection (min 4 chars):');
    if (!reason || reason.trim().length < 4) return;
    return act('Reject', () => apiPut(`/cash-advances/${advanceId}/reject?reason=${encodeURIComponent(reason.trim())}`));
  };
  const disburse = () => {
    const mode = window.prompt('Payment mode — cash / bank_transfer / upi / cheque:', 'cash');
    if (!mode) return;
    const ref = window.prompt('Reference no. (optional, e.g. UPI ref / cheque no.):') || '';
    return act('Disburse', () => apiPut(`/cash-advances/${advanceId}/disburse`, { payment_mode: mode.trim() || 'cash', reference_no: ref.trim() || null }));
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose}>
      <div
        className="bg-white w-full max-w-2xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="cash-advance-detail-drawer"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-display">Cash Advance</div>
            <div className="font-display font-bold text-lg font-mono">{adv?.advance_no || '…'}</div>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        {loading || !adv ? (
          <div className="p-10 text-center text-slate-400">
            <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Top facts */}
            <div className="grid grid-cols-2 gap-3">
              <Tile label="Amount" value={formatINR(adv.amount)} accent="indigo" />
              <Tile
                label="Outstanding"
                value={adv.status === 'Disbursed' || adv.status === 'Settled' ? formatINR(adv.outstanding_amount) : '—'}
                sub={adv.over_settlement_amount > 0 ? `Over by ${formatINR(adv.over_settlement_amount)}` : null}
                accent={adv.outstanding_amount > 0 ? 'rose' : 'emerald'}
              />
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-slate-500">Status:</span> <span className={`ml-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase border ${STATUS_BADGE[adv.status] || 'bg-slate-100'}`}>{adv.status}</span></div>
              <div><span className="text-slate-500">Employee:</span> <span className="ml-1 font-bold">{adv.employee_name}</span></div>
              <div><span className="text-slate-500">Project:</span> <span className="ml-1">{adv.project_name || '—'}</span></div>
              <div><span className="text-slate-500">Expected By:</span> <span className="ml-1">{fmtDate(adv.expected_settlement_date)}</span></div>
              <div className="col-span-2"><span className="text-slate-500">Purpose:</span> <span className="ml-1">{adv.purpose}</span></div>
              {adv.disbursed_at && (
                <>
                  <div><span className="text-slate-500">Disbursed:</span> <span className="ml-1">{fmtDateTime(adv.disbursed_at)}</span></div>
                  <div><span className="text-slate-500">Mode:</span> <span className="ml-1">{adv.disbursement_payment_mode}{adv.disbursement_reference_no ? ` · ${adv.disbursement_reference_no}` : ''}</span></div>
                </>
              )}
              {adv.reject_reason && (
                <div className="col-span-2 text-rose-700">
                  <span className="text-slate-500">Rejected:</span> {adv.reject_reason}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
              {isFinance && adv.status === 'Requested' && (
                <>
                  <ActBtn onClick={approve} icon={CheckCircle2} color="emerald" testId="cash-advance-approve-btn">Approve</ActBtn>
                  <ActBtn onClick={reject} icon={XCircle} color="rose" testId="cash-advance-reject-btn">Reject</ActBtn>
                </>
              )}
              {isFinance && adv.status === 'Approved' && (
                <ActBtn onClick={disburse} icon={ArrowDownToLine} color="indigo" testId="cash-advance-disburse-btn">Disburse Cash</ActBtn>
              )}
              {adv.status === 'Disbursed' && adv.outstanding_amount > 0 && (
                <ActBtn onClick={() => setShowSettle(true)} icon={Banknote} color="amber" testId="cash-advance-settle-cash-btn">Return Cash · Settle</ActBtn>
              )}
            </div>

            {/* Linked bills */}
            <div>
              <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5 mb-2">
                <Receipt size={12} /> Linked Petty Cash Bills · {(adv.linked_bills || []).length}
              </div>
              {(adv.linked_bills || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic">No bills filed against this advance yet.</div>
              ) : (
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
                      <tr>
                        <th className="text-left px-2 py-1.5">Date</th>
                        <th className="text-left px-2 py-1.5">Vendor</th>
                        <th className="text-right px-2 py-1.5">Amount</th>
                        <th className="text-center px-2 py-1.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adv.linked_bills.map(b => (
                        <tr key={b.id} className="border-t border-slate-100">
                          <td className="px-2 py-1.5">{b.expense_date}</td>
                          <td className="px-2 py-1.5 truncate max-w-[200px]">{b.vendor_name}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums font-bold">{formatINR(b.actual_amount)}</td>
                          <td className="px-2 py-1.5 text-center"><span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase bg-slate-100">{b.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Cash returns */}
            {(adv.cash_returns || []).length > 0 && (
              <div>
                <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5 mb-2">
                  <Banknote size={12} /> Cash Returns
                </div>
                <ul className="space-y-1 text-xs">
                  {adv.cash_returns.map(c => (
                    <li key={c.id} className="flex items-center justify-between border-b border-slate-100 pb-1">
                      <span>{fmtDateTime(c.at)} · {c.actor_name} · {c.payment_mode}</span>
                      <span className="font-bold tabular-nums">{formatINR(c.amount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Audit log */}
            <div>
              <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5 mb-2">
                <History size={12} /> Audit Trail · {(adv.audit_log || []).length}
              </div>
              <ul className="space-y-1.5 text-xs">
                {(adv.audit_log || []).map(e => (
                  <li key={e.id} className="border-l-2 border-slate-200 pl-3">
                    <div><span className="font-bold capitalize">{e.event.replace('_', ' ')}</span> · {fmtDateTime(e.at)}</div>
                    <div className="text-slate-500">by {e.actor_name || e.actor_email}{e.remarks ? ` — ${e.remarks}` : ''}{e.amount ? ` · ${formatINR(e.amount)}` : ''}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {showSettle && adv && (
        <SettleCashModal
          advance={adv}
          onClose={() => setShowSettle(false)}
          onSaved={() => { setShowSettle(false); refresh(); onAction(); }}
        />
      )}
    </div>
  );
}


function ActBtn({ onClick, icon: Icon, color, children, testId }) {
  const COLOR_MAP = {
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    rose: 'bg-rose-600 hover:bg-rose-700',
    indigo: 'bg-teal-600 hover:bg-teal-700',
    amber: 'bg-amber-600 hover:bg-amber-700',
  };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm flex items-center gap-1.5 text-white ${COLOR_MAP[color] || COLOR_MAP.indigo}`}
    >
      <Icon size={12} /> {children}
    </button>
  );
}


function SettleCashModal({ advance, onClose, onSaved }) {
  const [amount, setAmount] = useState(advance.outstanding_amount || 0);
  const [mode, setMode] = useState('cash');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const save = async () => {
    if (!amount || Number(amount) <= 0) return toast.error('Amount must be > 0');
    setSubmitting(true);
    try {
      await apiPost(`/cash-advances/${advance.id}/settle-cash`, {
        amount: Number(amount),
        payment_mode: mode,
        note: note || null,
      });
      toast.success('Cash settlement recorded');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record settlement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="cash-advance-settle-form">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-display font-bold text-lg">Return Cash</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-500">
            Outstanding: <span className="font-bold text-rose-600 tabular-nums">{formatINR(advance.outstanding_amount)}</span>
          </div>
          <Field label="Amount Returned (₹)">
            <input
              type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              data-testid="cash-advance-settle-amount"
            />
          </Field>
          <Field label="Payment Mode">
            <select
              value={mode} onChange={(e) => setMode(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              data-testid="cash-advance-settle-mode"
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </Field>
          <Field label="Note (optional)">
            <input
              value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              placeholder="e.g. Returned balance after Pune material run"
            />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button
            disabled={submitting} onClick={save}
            className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-sm font-display font-bold disabled:opacity-50"
            data-testid="cash-advance-settle-submit"
          >
            {submitting ? 'Saving…' : 'Record Settlement'}
          </button>
        </div>
      </div>
    </div>
  );
}
